import axios, { isAxiosError } from 'axios';
import { API_CONFIG } from '../config/api';
import { HomePageData } from '../types/home';
import { mockHomeData } from '../mocks/homeData';

// Интерфейс для ответа сервера
interface ServerMentalStateResponse {
  label: string;
  description: string;
  symptoms: Array<{
    text: string;
    description?: string;
  }>;
  quick_actions: {
    title: string;
    duration: string;
    type: string;
  }[];
  resources: {
    title: string;
    description: string;
    type: string;
  }[];
}

// Интерфейс для клиентского приложения
export interface MentalStateResponse {
  title: string;
  description: string;
  symptoms: string[];
  quickActions: {
    title: string;
    duration: string;
    type: string;
  }[];
  resources: {
    title: string;
    description: string;
    type: string;
  }[];
}

// Создаем экземпляр axios с базовыми настройками
const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Преобразование данных с сервера в формат клиента
const transformMentalStateData = (data: ServerMentalStateResponse): MentalStateResponse => {
  return {
    title: data.label,
    description: data.description,
    symptoms: data.symptoms.map(symptom => symptom.text),
    quickActions: data.quick_actions,
    resources: data.resources
  };
};

// Получение данных для главной страницы
export const getHomePageData = async (): Promise<HomePageData> => {
  try {
    const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.HOMEPAGE}`;
    console.log('Fetching homepage data from:', url);
    const response = await api.get<HomePageData>(url);
    console.log('Homepage data response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching homepage data:', error);
    if (isAxiosError(error)) {
      console.error('Axios error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });
    }
    // Временно возвращаем моковые данные при ошибке
    console.log('Falling back to mock data due to error');
    return mockHomeData;
  }
};

// Получение данных о психическом состоянии
export const getMentalState = async (stateCode: string): Promise<MentalStateResponse> => {
  const url = `${API_CONFIG.ENDPOINTS.EMOTIONAL_STATES}/${stateCode}/`;
  console.log('Requesting mental state URL:', url);
  
  try {
    const response = await api.get<ServerMentalStateResponse>(url);
    console.log('Mental state response:', response.data);
    return transformMentalStateData(response.data);
  } catch (error: unknown) {
    console.error('Error details:', error);
    if (isAxiosError(error)) {
      console.error('Axios error response:', error.response);
      if (error.response?.status === 404) {
        throw new Error('Состояние не найдено');
      }
    }
    throw new Error('Ошибка при загрузке данных');
  }
};

// Получение списка эмоциональных состояний
export const getEmotionalStates = async () => {
  try {
    const response = await api.get(API_CONFIG.ENDPOINTS.EMOTIONAL_STATES);
    return response.data;
  } catch (error) {
    console.error('Error fetching emotional states:', error);
    throw error;
  }
};

// Получение списка тем
export const getTopics = async () => {
  try {
    const response = await api.get(API_CONFIG.ENDPOINTS.TOPIC_DETAIL);
    return response.data;
  } catch (error) {
    console.error('Error fetching topics:', error);
    throw error;
  }
};

// Получение аудио практик
export const getAudioPractices = async () => {
  try {
    const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUDIO_PRACTICES}`;
    console.log('Fetching audio practices from:', url);
    const response = await api.get(url);
    console.log('Audio practices response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching audio practices:', error);
    if (isAxiosError(error)) {
      console.error('Axios error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });
    }
    throw error;
  }
};

// Получение списка курсов
export const getCourses = async () => {
  try {
    const response = await api.get(API_CONFIG.ENDPOINTS.COURSES);
    return response.data;
  } catch (error) {
    console.error('Error fetching courses:', error);
    throw error;
  }
};

// Получение поисковых подсказок
export const getSearchSuggestions = async () => {
  try {
    const response = await api.get(API_CONFIG.ENDPOINTS.SEARCH_SUGGESTIONS);
    return response.data;
  } catch (error) {
    console.error('Error fetching search suggestions:', error);
    throw error;
  }
};

// Получение детальной информации о теме
export const getTopicDetails = async (slug: string) => {
  try {
    const response = await api.get(`${API_CONFIG.ENDPOINTS.TOPIC_DETAIL}/${slug}/`);
    // Преобразуем данные в нужный формат, если они приходят в другом виде
    const data = response.data;
    return {
      ...data,
      audio_techniques: data.audio_techniques || [],
      related_courses: data.related_courses || []
    };
  } catch (error) {
    console.error('Error fetching topic details:', error);
    if (isAxiosError(error) && error.response?.status === 404) {
      throw new Error('Тема не найдена');
    }
    throw new Error('Ошибка при загрузке данных темы');
  }
};

// Получение детальной информации об аудио-практике
export const getAudioPracticeDetails = async (id: number) => {
  try {
    const url = API_CONFIG.ENDPOINTS.AUDIO_PRACTICE_DETAIL.replace(':id', id.toString());
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching audio practice details:', error);
    if (isAxiosError(error) && error.response?.status === 404) {
      throw new Error('Аудио-практика не найдена');
    }
    throw new Error('Ошибка при загрузке данных аудио-практики');
  }
};

// Получение детальной информации о курсе
export const getCourseDetails = async (id: string) => {
  try {
    const response = await api.get(`${API_CONFIG.ENDPOINTS.COURSES}/${id}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching course details:', error);
    if (isAxiosError(error) && error.response?.status === 404) {
      throw new Error('Курс не найден');
    }
    throw new Error('Ошибка при загрузке данных курса');
  }
};

export default api; 